import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import Button from 'tio-ui/components/button';
import Textarea from 'tio-ui/components/textarea';
import Avatar from 'tio-ui/components/avatar';
import Component from '@glimmer/component';
import { PhX, PhTrash, PhPencil, PhCheck } from 'ember-phosphor-icons';
import { not, eq } from 'tio-ui/utilities';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';

import type { TOC } from '@ember/component/template-only';
import type CommentModel from 'tio-data/models/comment';
import type PersonModel from 'tio-data/models/person';
import type Store from '@ember-data/store';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type UserModel from 'tio-data/models/user';

export interface TasCommentsContentSignature {
  Args: {
    commentable: TasApplicationModel | TasProgramInstanceModel;
    commentableType: string;
    authoredByPerson: PersonModel;
    authoredByUser?: UserModel;
    commentTags: string[];
    hideAddCommentForm?: boolean;
    initialTab?: 'approver' | 'employee';
    onTabChange?: (tab: 'approver' | 'employee') => void;
    showEmployeeTab?: boolean;
  };
  Element: HTMLElement;
}

// Comment Item Component
interface CommentItemSignature {
  Args: {
    comment: CommentModel;
    authoredByPerson: PersonModel;
    authoredByUser?: UserModel;
    onEdit: (comment: CommentModel) => void;
    onDelete: (comment: CommentModel) => void;
    editingCommentId: string | null;
    editingCommentText: string;
    onUpdateEditingComment: (event: Event) => void;
    onSubmitEditComment: (comment: CommentModel, event: Event) => void;
    onCancelEditingComment: () => void;
  };
  Element: HTMLElement;
}

class CommentItem extends Component<CommentItemSignature> {
  getCommentorName = (comment: CommentModel) => {
    const person = comment.person;
    if (person?.firstName && person?.lastName) {
      return `${person.firstName} ${person.lastName}`;
    }
    return person?.fullName || comment.commentor || 'Unknown';
  };

  getRelativeTime = (comment: CommentModel) => {
    const now = new Date();
    const createdAt = new Date(comment.createdAt);
    const diffInMs = now.getTime() - createdAt.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes} minutes ago`;
      }
      return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
    } else if (diffInDays === 1) {
      return '1 day ago';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else if (diffInDays < 14) {
      return '1 week ago';
    } else {
      const diffInWeeks = Math.floor(diffInDays / 7);
      return `${diffInWeeks} weeks ago`;
    }
  };

  canEditComment = (comment: CommentModel) => {
    return eq(this.args.authoredByPerson.id, comment.person?.id);
  };

  canDeleteComment = (_comment: CommentModel) => {
    return this.args.authoredByUser?.isTioAdmin;
  };

  <template>
    <div class="tio-flex group" data-test-comment-item>
      {{! Avatar - spans full height }}
      <div class="shrink-0">
        <Avatar @name={{this.getCommentorName @comment}} @size="sm" />
      </div>

      {{! Content area - name, timestamp, and comment }}
      <div class="tio-fill-min">
        {{! Header with name and timestamp }}
        <div class="tio-row-tight mb-1">
          <span class="tio-text-base-semibold tio-fg-primary">{{this.getCommentorName
              @comment
            }}</span>
          <span class="tio-text-caption">{{this.getRelativeTime @comment}}</span>
        </div>

        {{! Comment content }}
        <div>
          {{#if (eq @editingCommentId @comment.id)}}
            <form {{on "submit" (fn @onSubmitEditComment @comment)}} class="tio-col">
              <Textarea
                @value={{@editingCommentText}}
                {{on "input" @onUpdateEditingComment}}
                rows="3"
                class="w-full border tio-border-default rounded-lg p-3 resize-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                aria-label={{t "common.edit"}}
                data-test-edit-comment-textarea
              />

              <div class="tio-flex-tight">
                <Button
                  type="submit"
                  @intent="primary"
                  @size="sm"
                  title={{t "common.save"}}
                  data-test-save-comment-button
                >
                  <PhCheck @size="16" />
                </Button>
                <Button
                  type="button"
                  @appearance="outlined"
                  @intent="danger"
                  @size="sm"
                  {{on "click" @onCancelEditingComment}}
                  title={{t "common.cancel"}}
                  data-test-cancel-edit-button
                >
                  <PhX @size="16" />
                </Button>
              </div>
            </form>
          {{else}}
            <div class="tio-row-start-between">
              <div class="flex-1">
                <p
                  class="text-base tio-fg-secondary leading-relaxed whitespace-pre-wrap"
                >{{@comment.comment}}</p>
                {{#unless (eq @comment.createdAt @comment.updatedAt)}}
                  <div class="tio-text-caption mt-2">
                    {{t "common.tuition_assistance.last_updated"}}
                    {{safeFormatDate
                      @comment.updatedAt
                      month="long"
                      day="2-digit"
                      hour="numeric"
                      minute="numeric"
                    }}
                  </div>
                {{/unless}}
              </div>

              {{! Action buttons }}
              <div class="tio-flex-tight ml-4 shrink-0 self-center">
                {{#if (this.canEditComment @comment)}}
                  <Button
                    @appearance="minimal"
                    @intent="default"
                    @size="sm"
                    {{on "click" (fn @onEdit @comment)}}
                    title={{t "common.edit"}}
                    data-test-edit-comment-button
                  >
                    <PhPencil @size="16" class="tio-fg-secondary" />
                  </Button>
                {{/if}}
                {{#if (this.canDeleteComment @comment)}}
                  <Button
                    @appearance="minimal"
                    @intent="danger"
                    @size="sm"
                    {{on "click" (fn @onDelete @comment)}}
                    title={{t "common.delete"}}
                    data-test-delete-comment-button
                  >
                    <PhTrash @size="16" class="tio-fg-danger" />
                  </Button>
                {{/if}}
              </div>
            </div>
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}

// Comments List Component
interface CommentsListSignature {
  Args: {
    comments: CommentModel[];
    authoredByPerson: PersonModel;
    authoredByUser?: UserModel;
    onEdit: (comment: CommentModel) => void;
    onDelete: (comment: CommentModel) => void;
    editingCommentId: string | null;
    editingCommentText: string;
    onUpdateEditingComment: (event: Event) => void;
    onSubmitEditComment: (comment: CommentModel, event: Event) => void;
    onCancelEditingComment: () => void;
  };
  Element: HTMLElement;
}

const CommentsList: TOC<CommentsListSignature> = <template>
  {{#if @comments.length}}
    <div class="space-y-10" data-test-comments-list>
      {{#each @comments as |comment|}}
        <CommentItem
          @comment={{comment}}
          @authoredByPerson={{@authoredByPerson}}
          @authoredByUser={{@authoredByUser}}
          @onEdit={{@onEdit}}
          @onDelete={{@onDelete}}
          @editingCommentId={{@editingCommentId}}
          @editingCommentText={{@editingCommentText}}
          @onUpdateEditingComment={{@onUpdateEditingComment}}
          @onSubmitEditComment={{@onSubmitEditComment}}
          @onCancelEditingComment={{@onCancelEditingComment}}
        />
      {{/each}}
    </div>
  {{else}}
    <div class="text-center py-12" data-test-no-comments-message>
      <div class="tio-fg-muted text-sm">
        {{t "common.tuition_assistance.no_comments_found"}}
      </div>
    </div>
  {{/if}}
</template>;

// Add Comment Form Component
interface AddCommentFormSignature {
  Args: {
    commentable: TasApplicationModel | TasProgramInstanceModel | undefined;
    commentableType: string;
    authoredByPerson: PersonModel;
    commentTags: string[];
    onDidAddComment?: () => void;
    inDrawer?: boolean;
  };
  Element: HTMLElement;
}

export type { AddCommentFormSignature };

// Self-contained AddCommentForm component
class AddCommentFormComponent extends Component<AddCommentFormSignature> {
  @service declare store: Store;

  @tracked error: Error | null = null;

  submitComment = async (event: SubmitEvent) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const commentText = (new FormData(form).get('comment') as string)?.trim();

    if (!commentText) {
      return;
    }

    try {
      const comment = this.store.createRecord('comment', {
        commentable: this.args.commentable,
        commentableType: this.args.commentableType,
        createdAt: new Date().toISOString(),
        comment: commentText,
        person: this.args.authoredByPerson,
        tags: this.args.commentTags,
      }) as CommentModel;

      await comment.save();
      this.error = null;

      // Clear the form
      form.reset();

      // Notify parent component that a comment was added
      this.args.onDidAddComment?.();
    } catch (e) {
      this.error = e as Error;
    }
  };

  <template>
    <div class="w-full {{unless @inDrawer 'border-t tio-border-default p-6 tio-bg-surface'}}">
      <form {{on "submit" this.submitComment}} class="tio-col-tight w-full">
        <Textarea
          @name="comment"
          placeholder={{t "common.add_comment"}}
          rows="3"
          class="w-full border tio-border-default rounded-2xl p-4 resize-none tio-bg-sunken focus:tio-bg-surface focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
          aria-label={{t "common.add_comment"}}
          data-test-comment-textarea
        />

        {{#if this.error}}
          <p class="tio-fg-danger text-sm" data-test-comment-error>{{this.error.message}}</p>
        {{/if}}

        <Button
          type="submit"
          @intent="primary"
          @size="sm"
          disabled={{not @commentable}}
          data-test-add-comment-button
        >
          {{t "common.add_comment"}}
        </Button>
      </form>
    </div>
  </template>
}

export { AddCommentFormComponent as AddCommentForm };

export default class TasCommentsContentComponent extends Component<TasCommentsContentSignature> {
  @service declare store: Store;

  @tracked activeTab: 'approver' | 'employee' = this.args.initialTab ?? 'approver';
  @tracked newCommentText = '';
  @tracked error: Error | null = null;
  @tracked editingCommentId: string | null = null;
  @tracked editingCommentText = '';
  @tracked showDeleteConfirm = false;
  @tracked commentToDelete: CommentModel | null = null;

  get showEmployeeTab() {
    return this.args.showEmployeeTab !== false;
  }

  get approverComments() {
    const approverTags = this.args.commentTags.filter((tag) => tag !== 'Partner Messaging');
    const tagsToInclude = ['approver', ...approverTags];
    return this.args.commentable?.comments
      ? [...this.args.commentable.comments]
          .filter(
            (comment) =>
              !comment.isNew &&
              comment.tags?.some((tag) => tagsToInclude.includes(tag)) &&
              !comment.tags?.includes('Partner Messaging') &&
              !comment.tags?.includes('employee')
          )
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      : [];
  }

  get employeeComments() {
    const tagsToInclude = ['employee', 'Partner Messaging'];
    return this.args.commentable?.comments
      ? [...this.args.commentable.comments]
          .filter(
            (comment) => !comment.isNew && comment.tags?.some((tag) => tagsToInclude.includes(tag))
          )
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      : [];
  }

  get activeComments() {
    return this.activeTab === 'approver' ? this.approverComments : this.employeeComments;
  }

  get totalApproverComments() {
    return this.approverComments.length;
  }

  get totalEmployeeComments() {
    return this.employeeComments.length;
  }

  switchTab = (tab: 'approver' | 'employee') => {
    this.activeTab = tab;
    this.newCommentText = '';
    this.error = null;
    this.args.onTabChange?.(tab);
  };

  submitComment = async (event: SubmitEvent) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const commentText = (new FormData(form).get('comment') as string)?.trim();

    if (!commentText) {
      return;
    }

    try {
      const tags =
        this.activeTab === 'approver'
          ? ['approver', ...this.args.commentTags]
          : ['Partner Messaging']; // Use 'Partner Messaging' for approver-to-employee communication

      const comment = this.store.createRecord('comment', {
        commentable: this.args.commentable,
        commentableType: this.args.commentableType,
        createdAt: new Date().toISOString(),
        comment: commentText,
        person: this.args.authoredByPerson,
        tags: tags,
      }) as CommentModel;

      await comment.save();
      this.error = null;

      // Clear the form
      form.reset();
    } catch (e) {
      this.error = e as Error;
    }
  };

  saveComment = async (comment: CommentModel) => {
    await comment.save();
    this.editingCommentId = null;
    this.editingCommentText = '';
  };

  deleteComment = dropTask(async (comment: CommentModel) => {
    await comment.destroyRecord();
    this.showDeleteConfirm = false;
    this.commentToDelete = null;
  });

  rollbackComment = (comment: CommentModel) => {
    comment.rollbackAttributes();
    this.editingCommentId = null;
    this.editingCommentText = '';
  };

  startEditingComment = (comment: CommentModel) => {
    this.editingCommentId = comment.id;
    this.editingCommentText = comment.comment || '';
  };

  cancelEditingComment = () => {
    this.editingCommentId = null;
    this.editingCommentText = '';
  };

  updateEditingComment = (event: Event) => {
    const target = event.target as HTMLTextAreaElement;
    this.editingCommentText = target.value;
  };

  submitEditComment = async (comment: CommentModel, event: Event) => {
    event.preventDefault();
    comment.comment = this.editingCommentText;
    await this.saveComment(comment);
  };

  confirmDeleteComment = (comment: CommentModel) => {
    this.commentToDelete = comment;
    this.showDeleteConfirm = true;
  };

  cancelDeleteComment = () => {
    this.showDeleteConfirm = false;
    this.commentToDelete = null;
  };

  <template>
    <div class="tio-col-flush h-full tio-bg-surface" data-test-comments-container>
      {{! Tab Bar }}
      <div
        class="tio-flex-center border-b-2 tio-border-default mb-6 tio-bg-surface"
        data-test-comments-tabs
      >
        <div class="flex">
          <button
            type="button"
            class="px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors
              {{if
                (eq this.activeTab 'approver')
                'text-purple-600 border-b-2 border-purple-600'
                'tio-fg-muted hover:tio-fg-secondary'
              }}"
            {{on "click" (fn this.switchTab "approver")}}
            data-test-approver-comments-tab
          >
            {{t "common.tuition_assistance.approver_comments"}}
            {{#if this.totalApproverComments}}
              <span class="ml-1 text-xs">({{this.totalApproverComments}})</span>
            {{/if}}
          </button>
          {{#if this.showEmployeeTab}}
            <button
              type="button"
              class="px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors
                {{if
                  (eq this.activeTab 'employee')
                  'text-purple-600 border-b-2 border-purple-600'
                  'tio-fg-muted hover:tio-fg-secondary'
                }}"
              {{on "click" (fn this.switchTab "employee")}}
              data-test-employee-comments-tab
            >
              {{t "common.tuition_assistance.employee_comments"}}
              {{#if this.totalEmployeeComments}}
                <span class="ml-1 text-xs">({{this.totalEmployeeComments}})</span>
              {{/if}}
            </button>
          {{/if}}
        </div>
      </div>

      {{! Content }}
      <div class="tio-col-flush flex-1 min-h-0 tio-bg-surface">
        {{! Comments List }}
        <div class="flex-1 overflow-y-auto px-6 py-8 tio-bg-surface">
          <CommentsList
            @comments={{this.activeComments}}
            @authoredByPerson={{@authoredByPerson}}
            @authoredByUser={{@authoredByUser}}
            @onEdit={{this.startEditingComment}}
            @onDelete={{this.confirmDeleteComment}}
            @editingCommentId={{this.editingCommentId}}
            @editingCommentText={{this.editingCommentText}}
            @onUpdateEditingComment={{this.updateEditingComment}}
            @onSubmitEditComment={{this.submitEditComment}}
            @onCancelEditingComment={{this.cancelEditingComment}}
          />
        </div>

        {{! Add Comment Form - Sticky at bottom }}
        {{#unless @hideAddCommentForm}}
          <div
            class="sticky bottom-0 border-t tio-border-default p-6 tio-bg-surface z-10"
            data-test-add-comment-form
          >
            <form {{on "submit" this.submitComment}} class="tio-col-loose">
              <Textarea
                @name="comment"
                placeholder={{t "common.add_comment"}}
                rows="4"
                class="w-full border tio-border-default rounded-2xl p-4 resize-none tio-bg-sunken focus:tio-bg-surface focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                aria-label={{t "common.add_comment"}}
              />

              {{#if this.error}}
                <p class="tio-fg-danger text-sm">{{this.error.message}}</p>
              {{/if}}

              <Button type="submit" @intent="primary" @size="sm" disabled={{not @commentable}}>
                {{t "common.add_comment"}}
              </Button>
            </form>
          </div>
        {{/unless}}
      </div>
    </div>

    <ConfirmationDialog
      @isOpen={{this.showDeleteConfirm}}
      @title="Delete Comment"
      @intent="danger"
      @onConfirm={{fn this.deleteComment.perform this.commentToDelete}}
      @onCancel={{this.cancelDeleteComment}}
      @isRunning={{this.deleteComment.isRunning}}
      @confirmText={{t "common.delete"}}
    >
      <p>{{t "common.tuition_assistance.are_you_sure_you_want_to_delete"}}</p>
      <p>{{t "common.tuition_assistance.this_action_cannot_be_undone"}}</p>
    </ConfirmationDialog>
  </template>
}
